import { ReduxSimpleActionInterface } from '@ecommerce/nest-ssr/dist/common';
import { ProductBoxWideProps } from 'modules/shared/components/product/product-box-wide/product-box-wide.props';
import { ProductBoxNarrowProps } from 'modules/shared/components/product/product-box-narrow/product-box-narrow.props';
import { CategoryProductsInterface } from 'modules/categories/views/pages/category-regular/category-regular-page.props';
import {
    PRODUCTS_BETWEEN_ROWS,
    PRODUCTS_PER_ROW
} from 'helpers/ads-config';

type SortedProduct = ProductBoxWideProps | ProductBoxNarrowProps;
type SortedProducts = ProductBoxWideProps[] | ProductBoxNarrowProps[];

export enum ProductsActions {
    ADD_PRODUCT = 'ADD_PRODUCT',
    ADD_PRODUCTS = 'ADD_PRODUCTS'
}

export const sponsoredProductsSorter = (
    productA: SortedProduct,
    productB: SortedProduct
): number => {
    if (productA.sponsored && productB.sponsored) {
        if ((productA.sponsoredPosition ?? 0) < (productB.sponsoredPosition ?? 0)) {
            return -1;
        } else if ((productA.sponsoredPosition ?? 0) > (productB.sponsoredPosition ?? 0)) {
            return 1;
        }
    } else if (productA.sponsored) {
        return -1;
    } else if (productB.sponsored) {
        return 1;
    }

    return 0;
};

export const splitProductsByType = (products: SortedProducts): {
    regularProducts: SortedProducts;
    sponsoredProducts: SortedProducts;
} => {
    const regularProducts: SortedProducts = [];
    const sponsoredProducts: SortedProducts = [];

    for (const product of products) {
        if (product.sponsoredPosition) {
            sponsoredProducts.push(product);
        } else {
            regularProducts.push(product);
        }
    }

    return {
        regularProducts,
        sponsoredProducts
    };
};

export function divideSponsoredProducts(products: SortedProduct[]): SortedProduct[][] {
    const positionToProductMap = new Map<number, SortedProduct[]>();

    for (const product of products) {
        if (positionToProductMap.has(product.sponsoredPosition ?? 0)) {
            const positionItems = positionToProductMap.get(product.sponsoredPosition ?? 0) ?? [];

            positionItems.push(product);

            positionToProductMap.set(product.sponsoredPosition ?? 0, positionItems);
        } else {
            positionToProductMap.set(product.sponsoredPosition ?? 0, [product]);
        }
    }

    return [...positionToProductMap.entries()].sort((a, b) => a?.[0] - b?.[0]).map(item => item?.[1]);
}

export const products = [
    (
        state: CategoryProductsInterface<SortedProduct>,
        action: ReduxSimpleActionInterface<ProductsActions.ADD_PRODUCT, { payload: { product: SortedProduct; }; }> |
            ReduxSimpleActionInterface<ProductsActions.ADD_PRODUCTS, { payload: { products: SortedProducts; }; }>
    ): CategoryProductsInterface<SortedProduct> => {
        switch (action.type) {
            case ProductsActions.ADD_PRODUCT:
                return {
                    ...state,
                    items: [
                        action.payload.product,
                        ...state.items.filter(item => item.id !== action.payload.product.id)
                    ]
                };
            case ProductsActions.ADD_PRODUCTS: {
                if (!action.payload.products?.length) {
                    return state;
                }

                const combinedProducts: SortedProduct[] = [];

                const {
                    regularProducts,
                    sponsoredProducts
                } = splitProductsByType(state.items);

                sponsoredProducts.push(...action.payload.products);

                const regularProductsFiltered = regularProducts
                    .filter(regularItem => !sponsoredProducts.find(sponsoredItem => sponsoredItem.id === regularItem.id));

                const injectedProductsParts = divideSponsoredProducts(sponsoredProducts);

                for (const injectedPart of injectedProductsParts) {
                    combinedProducts.push(...injectedPart);
                    combinedProducts.push(
                        ...regularProductsFiltered.splice(
                            0,
                            PRODUCTS_BETWEEN_ROWS + ((injectedPart.length % PRODUCTS_PER_ROW) ? 4 - (injectedPart.length % PRODUCTS_PER_ROW) : 0)
                        )
                    );
                }

                if (regularProductsFiltered.length) {
                    combinedProducts.push(...regularProductsFiltered);
                }

                return {
                    ...state,
                    items: combinedProducts
                };
            }
            default:
                return state;
        }
    }
];
